import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);
const KEY = "gu_cart_v1";

// Cart contents (non-sensitive) persist in localStorage so buyers don't lose items
// on refresh. No credentials or tokens are stored here.
function readInitial() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(readInitial);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch {
      // localStorage may be unavailable (Safari private mode, quota). Non-fatal.
    }
  }, [items]);

  const add = useCallback((plant, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === plant.id);
      if (existing) {
        return prev.map((i) => (i.id === plant.id ? { ...i, quantity: i.quantity + qty } : i));
      }
      return [
        ...prev,
        { id: plant.id, slug: plant.slug, name: plant.name, price: plant.price, image: plant.images?.[0], quantity: qty },
      ];
    });
    setOpen(true);
  }, []);

  const remove = useCallback((id) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const setQty = useCallback(
    (id, qty) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, qty) } : i))),
    [],
  );
  const clear = useCallback(() => setItems([]), []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, add, remove, setQty, clear, subtotal, count, open, setOpen }),
    [items, add, remove, setQty, clear, subtotal, count, open],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
